/* External */
import {
  memo,
  createContext,
  useEffect,
  useContext,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

/* Util */
import { useIsForegroundApp } from '../ForegroundAppProvider';

export const CurrentTimeContext = createContext(Math.floor(Date.now() / 60_000));

/* The context value is epoch MINUTES (Math.floor(now / 60_000)), not a Date:
   it changes at most once per minute and stays a primitive so consumers that
   only need minute granularity don't churn on object identity. Consumers that
   need a Date derive it as `new Date(minutes * 60_000)`. */
export function useCurrentTime() {
  return useContext(CurrentTimeContext);
}

function CurrentTimeProvider({ children }) {
  const [currentTime, setCurrentTime] = useState(() => Math.floor(Date.now() / 60_000));
  const isForegroundApp = useIsForegroundApp();

  useEffect(() => {
    let interval;
    let timeout;
    if (isForegroundApp) {
      const current = new Date();
      setCurrentTime(Math.floor(current.getTime() / 60_000));
      const milliSecondsLeft = 1000 - current.getMilliseconds();
      const secondsLeft = 60 - current.getSeconds();
      const milliSecondsTotalLeft = milliSecondsLeft + (secondsLeft * 1000);
      timeout = setTimeout(() => {
        interval = setInterval(() => {
          setCurrentTime(Math.floor(Date.now() / 60_000));
        }, minutesToMilliseconds(1));
      }, milliSecondsTotalLeft);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [isForegroundApp]);

  return (
    <CurrentTimeContext.Provider value={currentTime}>
      {children}
    </CurrentTimeContext.Provider>
  );
}

CurrentTimeProvider.propTypes = {
  children: PropTypes.node,
};

export default memo(CurrentTimeProvider);
